(() => {
  class OkazoCartShipping extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      const cartItems = this.closest("okazo-cart-page")?.querySelector("cart-items");
      this.cartUpdating = Boolean(cartItems?.updating);
      this.cartNeedsRefresh = Boolean(cartItems?.needsRefresh);
      this.form = this.querySelector("form");
      this.button = this.form.querySelector('button[type="submit"], button');
      this.fields = this.querySelector("[data-address-fields]");
      this.destination = this.querySelector("[data-destination]");
      this.estimate = this.querySelector("[data-estimate]");
      this.status = this.querySelector("[data-status]");
      this.rates = this.querySelector("[data-rates]");
      this.rateDetails = this.querySelector("[data-rate-details]");
      this.country = this.querySelector("[data-country]");
      this.province = this.querySelector("[data-province]");
      if (!this.initialized) {
        const country = [...this.country.options].find(
          (option) => option.value === this.country.dataset.default || option.text === this.country.dataset.default,
        );
        if (country) country.selected = true;
        this.setupProvinces();
        this.initialized = true;
      }
      this.form.addEventListener(
        "submit",
        (event) => {
          event.preventDefault();
          this.calculate();
        },
        { signal },
      );
      this.querySelector("[data-edit]")?.addEventListener(
        "click",
        () => {
          this.invalidate();
          this.querySelector("[data-zip]").focus();
        },
        { signal },
      );
      this.form.addEventListener("input", () => this.invalidate(), { signal });
      this.country.addEventListener("change", () => this.setupProvinces(), { signal });
      this.querySelector("[data-retry]")?.addEventListener(
        "click",
        () => this.calculate({ automatic: true, focusResult: true }),
        {
          signal,
        },
      );
      document.addEventListener(
        "okazo:cart-updating",
        () => {
          this.cartUpdating = true;
          this.invalidate({ preserveDestination: true });
          if (this.hasDestination) this.status.textContent = "Mise à jour du panier…";
        },
        { signal },
      );
      document.addEventListener(
        "okazo:cart-settled",
        (event) => {
          this.cartUpdating = false;
          this.cartNeedsRefresh = event.detail.needsRefresh;
          this.setLoading(false);
          if (this.cartNeedsRefresh) {
            if (this.hasDestination) this.status.textContent = "Actualisez le panier avant de recalculer la livraison.";
            return;
          }
          this.refreshForCart();
        },
        { signal },
      );
      for (const name of ["afterCartChanged", "afterUpdateQuantity"]) {
        document.addEventListener(
          name,
          () => {
            if (!this.cartUpdating) this.refreshForCart();
          },
          { signal },
        );
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.invalidate();
    }

    setupProvinces() {
      const provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || "[]");
      this.province.replaceChildren(...provinces.map(([value, label]) => new Option(label, value)));
      this.querySelector("[data-province-wrap]").hidden = provinces.length === 0;
    }

    refreshForCart() {
      const state = this.closest("okazo-cart-page")?.querySelector("[data-cart-state]");
      if (state?.dataset.itemCount === "0") {
        this.invalidate();
        return;
      }
      if (this.hasDestination) this.calculate({ automatic: true });
      else this.invalidate();
    }

    invalidate({ preserveDestination = false } = {}) {
      const hadEstimate = Boolean(this.request) || !this.rates.hidden;
      this.request?.abort();
      this.request = null;
      this.rates.hidden = true;
      if (this.rateDetails) {
        this.rateDetails.hidden = true;
        this.rateDetails.open = false;
      }
      if (!preserveDestination) this.hasDestination = false;
      if (this.fields) this.fields.hidden = Boolean(this.hasDestination);
      if (this.destination) this.destination.hidden = !this.hasDestination;
      if (this.estimate) this.estimate.hidden = true;
      const retry = this.querySelector("[data-retry]");
      if (retry) retry.hidden = true;
      this.rates.replaceChildren();
      this.status.classList.remove("visually-hidden");
      this.status.textContent = hadEstimate
        ? "Le panier ou la destination a changé. Recalculez les frais de livraison."
        : "";
      this.setLoading(false);
    }

    setLoading(loading) {
      this.button.disabled = Boolean(loading || this.cartUpdating || this.cartNeedsRefresh);
      const retry = this.querySelector("[data-retry]");
      if (retry) retry.disabled = this.button.disabled;
      this.querySelector("[data-label]").textContent = loading ? "Calcul en cours…" : "Calculer les frais";
      this.querySelector(".okz-shipping-spinner").hidden = !loading;
      const spinner = this.querySelector("[data-refresh-spinner]");
      if (spinner) spinner.hidden = !(loading && this.hasDestination);
      this.rates.setAttribute("aria-busy", String(loading));
    }

    showEstimate(rates) {
      const state = this.closest("okazo-cart-page")?.querySelector("[data-cart-state]");
      if (!state || !this.estimate) return;
      const comparable = rates.filter((rate) => !rate.currency || rate.currency === this.dataset.currency);
      if (!comparable.length) return;
      const cheapest = comparable.reduce((best, rate) => (Number(rate.price) < Number(best.price) ? rate : best));
      const subtotal = Number(state.dataset.totalPrice);
      if (!Number.isFinite(subtotal)) return;
      const total = subtotal + Math.round(Number(cheapest.price) * 100);
      const from = comparable.length > 1 ? " dès" : "";
      this.querySelector("[data-estimate-label]").textContent =
        state.dataset.taxesIncluded === "true" ? `Total estimé${from}` : `Articles + livraison${from}`;
      this.querySelector("[data-estimate-total]").textContent = new Intl.NumberFormat(
        document.documentElement.lang || "fr-FR",
        { style: "currency", currency: this.dataset.currency },
      ).format(total / 100);
      this.querySelector("[data-estimate-method]").textContent = `Avec ${cheapest.presentment_name || cheapest.name}.`;
      this.estimate.hidden = false;
    }

    async calculate({ automatic = false, focusResult = !automatic } = {}) {
      if (this.cartUpdating || this.cartNeedsRefresh) return;
      if (!(automatic ? this.form.checkValidity() : this.form.reportValidity())) return;
      this.invalidate({ preserveDestination: automatic });
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(new DOMException("Shipping timeout", "TimeoutError")), 25000);
      this.setLoading(true);
      this.status.textContent = "Recherche des tarifs pour votre panier…";
      const address = {
        country: this.country.value,
        province: this.province.value,
        zip: this.querySelector("[data-zip]").value.trim(),
      };
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(address)) params.set(`shipping_address[${key}]`, value);
      const read = async (url, options = {}) => {
        const response = await fetch(url, {
          ...options,
          signal: request.signal,
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error(response.status === 422 ? "address" : "network");
        return response.json();
      };
      try {
        await read(`${this.dataset.cartUrl}/prepare_shipping_rates.json?${params}`, { method: "POST" });
        let result = null;
        for (let attempt = 0; attempt < 12 && result === null; attempt++) {
          if (attempt) await new Promise((resolve) => setTimeout(resolve, 1500));
          request.signal.throwIfAborted();
          result = await read(`${this.dataset.cartUrl}/async_shipping_rates.json?${params}`);
        }
        if (!Array.isArray(result?.shipping_rates)) throw new Error("network");
        if (this.request !== request) return;
        if (
          result.shipping_rates.some(
            (rate) =>
              rate.price == null ||
              String(rate.price).trim() === "" ||
              !Number.isFinite(Number(rate.price)) ||
              Number(rate.price) < 0,
          )
        )
          throw new Error("network");
        this.rates.replaceChildren(
          ...result.shipping_rates.map((rate) => {
            const item = document.createElement("li");
            const name = document.createElement("span");
            name.textContent = rate.presentment_name || rate.name;
            const price = document.createElement("strong");
            price.textContent = new Intl.NumberFormat(document.documentElement.lang || "fr-FR", {
              style: "currency",
              currency: rate.currency || this.dataset.currency,
            }).format(Number(rate.price));
            item.append(name, price);
            return item;
          }),
        );
        this.rates.hidden = !result.shipping_rates.length;
        if (this.rateDetails) this.rateDetails.hidden = this.rates.hidden;
        if (result.shipping_rates.length && this.destination) {
          this.hasDestination = true;
          this.fields.hidden = true;
          this.destination.hidden = false;
          this.querySelector("[data-destination-label]").textContent = [
            address.zip,
            this.province.selectedOptions[0]?.text,
            this.country.selectedOptions[0]?.text,
          ]
            .filter(Boolean)
            .join(" · ");
          this.showEstimate(result.shipping_rates);
          if (focusResult) this.querySelector("[data-edit]").focus({ preventScroll: true });
        }
        if (!result.shipping_rates.length) this.invalidate({ preserveDestination: automatic });
        this.status.classList.toggle("visually-hidden", result.shipping_rates.length > 0);
        this.status.textContent = result.shipping_rates.length
          ? "Frais de livraison estimés"
          : "Aucun tarif disponible pour cette destination. Vérifiez votre adresse ou consultez les options au paiement.";
      } catch (error) {
        if (this.request !== request) return;
        const retry = this.querySelector("[data-retry]");
        if (retry) retry.hidden = !this.hasDestination;
        this.status.textContent =
          error.message === "address"
            ? "Vérifiez le pays, la région et le code postal, puis réessayez."
            : "Les tarifs sont indisponibles pour le moment. Réessayez ou consultez-les au paiement.";
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.request = null;
          this.setLoading(false);
        }
      }
    }
  }
  if (!customElements.get("okazo-cart-shipping")) customElements.define("okazo-cart-shipping", OkazoCartShipping);
})();
